import { useMemo } from 'react';
import { buildRaidSettlementReport } from '../domain/raidSettlementReport.js';
import '../../../styles/raid-settlement-report.css';
import '../../../styles/raid-settlement-report-content.css';
import '../../../styles/raid-settlement-report-members.css';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

function formatEok(value) {
  const number = Number(value || 0);
  if (number >= 10000) {
    const jo = Math.floor(number / 10000);
    const eok = Math.round(number % 10000);
    return eok ? `${jo}조 ${eok.toLocaleString('ko-KR')}억` : `${jo}조`;
  }
  return `${number.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}억`;
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function NikkePortrait({ row }) {
  return (
    <span className="raid-settlement-report-nikke-image">
      {row?.imageUrl ? <img src={siteImageUrl(row.imageUrl)} alt="" loading="lazy" /> : <b>{String(row?.name || row?.code || '?').slice(0, 1)}</b>}
    </span>
  );
}

function Metric({ label, value, detail = '', strong = false }) {
  return (
    <article className={`raid-settlement-report-metric ${strong ? 'primary' : ''}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {detail ? <small>{detail}</small> : null}
    </article>
  );
}

function SettlementSummary({ settlement, report }) {
  return (
    <section className="raid-settlement-report-summary">
      <header>
        <div><h3>차수 핵심 요약</h3><span>실전 기록에서 바로 계산한 운영 지표</span></div>
        <small>{report.attackCount} / {report.attackCapacity || 0}타 사용</small>
      </header>
      <div className="raid-settlement-report-metrics">
        <Metric label="유니온 총딜" value={formatEok(settlement.totalDamageEok)} detail={`보정 총딜 ${formatEok(settlement.totalWeightedDamageEok)}`} strong />
        <Metric label="평균 1타" value={formatEok(report.averageDamageEok)} detail={`${report.attackCount}타 기준`} />
        <Metric label="공격 소진율" value={formatPercent(report.attackUsePercent)} detail={`${report.attackCount}/${report.attackCapacity || 0}타`} />
        <Metric label="참여율" value={formatPercent(report.participationPercent)} detail={`${report.participantCount}/${report.memberCount}명`} />
        <Metric label="3타 완료율" value={formatPercent(report.completionPercent)} detail={`${report.completedMemberCount}/${report.memberCount}명`} />
        <Metric label="막타" value={`${report.killCount}회`} detail={`처리 보스 ${report.clearedBossCount}/5`} />
      </div>
      <div className="raid-settlement-report-records">
        <article>
          <span>보정 기여 1위</span>
          <strong>{report.topContributor?.nickname || '-'}</strong>
          <small>{report.topContributor ? `${formatPercent(report.topContributor.contributionPercent)} · ${formatEok(report.topContributor.totalDamageEok)}` : '기록 없음'}</small>
        </article>
        <article>
          <span>최고 단일 공격</span>
          <strong>{report.topAttack?.nickname || '-'}</strong>
          <small>{report.topAttack ? `${formatEok(report.topAttack.damageEok)} · ${report.topAttack.bossName || '보스명 확인 불가'}` : '기록 없음'}</small>
        </article>
      </div>
    </section>
  );
}

function SettlementNikkeUsage({ report }) {
  return (
    <section className="raid-settlement-report-nikke">
      <header><div><h3>니케 사용 현황</h3><span>전체 실전 덱 사용 횟수와 범용성 · 같은 공격 내 중복은 1회만 집계</span></div><small>전체 TOP 10</small></header>
      <div className="raid-settlement-report-nikke-layout">
        <div className="raid-settlement-report-nikke-ranking">
          {report.nikkeUsage.length ? report.nikkeUsage.map((row, index) => (
            <article key={row.code}>
              <b className="rank">{index + 1}</b>
              <NikkePortrait row={row} />
              <div className="copy">
                <strong>{row.name || row.code}</strong>
                <span>{row.count}회 · 전체 공격의 {formatPercent(row.usagePercent)}</span>
                <small>{row.memberCount}명 사용 · {row.bossCount}개 보스</small>
              </div>
            </article>
          )) : <p className="raid-settlement-report-empty">덱 정보가 포함된 실전 기록 없음</p>}
        </div>
        <div className="raid-settlement-report-boss-meta">
          {report.bossNikkeUsage.map((boss) => (
            <article key={boss.bossNo}>
              <header><strong>{boss.bossName}</strong><span>TOP 3</span></header>
              <div>
                {boss.rows.length ? boss.rows.map((row, index) => (
                  <p key={row.code}>
                    <b>{index + 1}</b>
                    <NikkePortrait row={row} />
                    <span><strong>{row.name || row.code}</strong><small>{row.count}회 · {formatPercent(row.usagePercent)}</small></span>
                  </p>
                )) : <small className="raid-settlement-report-empty">기록 없음</small>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SettlementMemberHighlights({ report }) {
  return (
    <section className="raid-settlement-report-members">
      <header><div><h3>멤버 핵심 기록</h3><span>보정 기여도 순</span></div><small>TOP {report.memberHighlights.length}</small></header>
      <div>
        {report.memberHighlights.map((row) => (
          <article key={row.memberKey}>
            <div className="title"><b>#{row.rank}</b><strong>{row.nickname}</strong><em>{formatPercent(row.contributionPercent)}</em></div>
            <dl>
              <div><dt>실제 총딜</dt><dd>{formatEok(row.totalDamageEok)}</dd></div>
              <div><dt>평균 1타</dt><dd>{formatEok(row.averageDamageEok)}</dd></div>
              <div><dt>최고타</dt><dd>{formatEok(row.topDamageEok)}</dd></div>
              <div><dt>공격</dt><dd>{row.attackCount}/3</dd></div>
              <div><dt>공격 보스</dt><dd>{row.bossCount}개</dd></div>
              <div><dt>막타</dt><dd>{row.killCount}회</dd></div>
            </dl>
          </article>
        ))}
      </div>
    </section>
  );
}

export function RaidSettlementReport({ settlement }) {
  const report = useMemo(() => buildRaidSettlementReport(settlement), [settlement]);
  return (
    <>
      <SettlementSummary settlement={settlement} report={report} />
      <SettlementNikkeUsage report={report} />
      <SettlementMemberHighlights report={report} />
    </>
  );
}
